import {
  Dialog,
  DialogPanel,
  DialogTitle,
  DialogBackdrop,
} from '@headlessui/react';
import { useStoreApi, useReactFlow } from '@xyflow/react';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { Button } from 'src/components/button';
import { CloseIon } from 'src/components/icons/CloseIcon';
import { LoadingIcon } from 'src/components/icons/LoadingIcon';
import { defaultProjectData } from 'src/config';
import { useGlobalContext } from 'src/context/global/use-global-context';
import { PaperSize, ExportResolution } from 'src/types';
import { ExportDataType, exportDataAsBlob } from 'src/utils/export-data';
import { ExportFile, downloadFile, downloadAsZip } from 'src/utils/export-zip';
import { twMerge } from 'tailwind-merge';

import { PaperSizeSelector } from './shared/paper-size-selector';
import { ResolutionSelector } from './shared/resolution-selector';

type BlobResult = { blob: Blob; filename: string };
type DataFormat = 'excel' | 'pdf';

interface Props {
  onExportFrame: (type: 'pdf') => void;
  onExportScreenshot: () => void;
  onGetFrameBlob: () => Promise<BlobResult | null>;
  onGetScreenshotBlob: () => Promise<BlobResult | null>;
}

type ExportItemId =
  | 'connection-diagram'
  | 'switches'
  | 'ip-vlan'
  | 'port-assignment'
  | 'cable-type'
  | 'screenshot';

const DATA_IDS: ExportItemId[] = [
  'switches',
  'ip-vlan',
  'port-assignment',
  'cable-type',
];

const EXPORT_ITEMS: {
  id: ExportItemId;
  label: string;
  fixedFormat?: string;
}[] = [
  { id: 'connection-diagram', label: 'Connection Diagram', fixedFormat: 'PDF' },
  { id: 'switches', label: 'List of Switches' },
  { id: 'ip-vlan', label: 'Device IP / VLAN List' },
  { id: 'port-assignment', label: 'Port Assignment' },
  { id: 'cable-type', label: 'Cables List' },
  { id: 'screenshot', label: 'Screenshot', fixedFormat: 'PNG' },
];

const DATA_EXPORTS: [ExportItemId, ExportDataType][] = [
  ['switches', ExportDataType.SWITCHES],
  ['ip-vlan', ExportDataType.DEVICE_IP_VLAN],
  ['port-assignment', ExportDataType.PORT_ASSIGNMENT],
  ['cable-type', ExportDataType.CABLE_TYPE],
];

export default function DialogExportDocumentation({
  onExportFrame,
  onExportScreenshot,
  onGetFrameBlob,
  onGetScreenshotBlob,
}: Props) {
  const { setShowDialogs, vlans, projectData, setExportSettings } =
    useGlobalContext();
  const { getNodes, getEdges } = useReactFlow();
  const store = useStoreApi();

  const [selected, setSelected] = useState<Set<ExportItemId>>(
    new Set(EXPORT_ITEMS.map((i) => i.id)),
  );
  const [dataFormat, setDataFormat] = useState<DataFormat>('excel');
  const [isExporting, setIsExporting] = useState(false);

  const currentPaperSize =
    projectData?.exportSettings?.paperSize ?? ('A4' as PaperSize);
  const currentResolution =
    projectData?.exportSettings?.resolution ?? ('medium' as ExportResolution);

  const exportLabel = selected.size > 1 ? 'Export as ZIP' : 'Export';

  const onClose = () => setShowDialogs({ exportDocumentation: false });

  const toggleItem = (id: ExportItemId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const getItemFormat = (item: (typeof EXPORT_ITEMS)[number]) => {
    if (item.fixedFormat) return item.fixedFormat;
    return dataFormat === 'excel' ? 'Excel (.xlsx)' : 'PDF';
  };

  const handleExport = async () => {
    if (isExporting || selected.size === 0) return;
    setIsExporting(true);

    try {
      store.getState().resetSelectedElements();
      await new Promise<void>((r) =>
        requestAnimationFrame(() => requestAnimationFrame(() => r())),
      );

      const isSingle = selected.size === 1;

      if (isSingle) {
        const nodes = getNodes();
        const edges = getEdges();
        const project = projectData || defaultProjectData;

        if (selected.has('connection-diagram')) onExportFrame('pdf');
        else if (selected.has('screenshot')) onExportScreenshot();
        else {
          const [[, dataType]] = DATA_EXPORTS.filter(([id]) =>
            selected.has(id),
          );
          await exportDataAsBlob(
            dataType,
            dataFormat,
            nodes,
            edges,
            vlans,
            project,
          ).then((r) => r && downloadFile(r));
        }
        toast.success('Export completed successfully.');
        onClose();
        return;
      }

      const nodes = getNodes();
      const edges = getEdges();
      const project = projectData || defaultProjectData;
      const collected: ExportFile[] = [];

      if (selected.has('connection-diagram')) {
        const result = await onGetFrameBlob();
        if (result) collected.push(result);
      }

      const dataResults = await Promise.all(
        DATA_EXPORTS.filter(([id]) => selected.has(id)).map(([, dataType]) =>
          exportDataAsBlob(dataType, dataFormat, nodes, edges, vlans, project),
        ),
      );
      dataResults.forEach((r) => {
        if (r) collected.push(r);
      });

      if (selected.has('screenshot')) {
        const result = await onGetScreenshotBlob();
        if (result) collected.push(result);
      }

      if (collected.length === 0) {
        toast.warning('No data available to export.');
        return;
      }

      await downloadAsZip(collected, project.projectName || 'export');
      toast.success('Export completed successfully.');
      onClose();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Export failed:', error);
      toast.error('Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const hasDataItemSelected = DATA_IDS.some((id) => selected.has(id));

  return (
    <Dialog
      open
      onClose={onClose}
      className="relative z-50"
      data-testid="dialog-export-documentation"
    >
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />
      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <DialogPanel
            transition
            data-testid="dialog-export-documentation-panel"
            className={twMerge([
              'relative w-full max-w-2xl transform rounded-lg bg-white px-6 pb-6 pt-5 text-left shadow-xl transition-all',
              'data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95',
            ])}
          >
            <DialogTitle
              as="h3"
              className="pb-4 text-xl font-bold text-gray-900"
              data-testid="dialog-export-documentation-title"
            >
              Export Documentation
            </DialogTitle>
            <div className="absolute right-4 top-4">
              <button
                type="button"
                onClick={onClose}
                data-testid="dialog-export-documentation-close-button"
                className="text-gray-400 hover:text-gray-500"
              >
                <span className="sr-only">Close</span>
                <CloseIon className="h-6 w-6" />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-6">
              {/* Left: Export items */}
              <div>
                <p className="mb-3 text-sm font-medium text-gray-900">
                  Select items to export
                </p>
                <div className="space-y-3">
                  {EXPORT_ITEMS.map((item) => (
                    <label
                      key={item.id}
                      className="flex cursor-pointer items-center gap-3 rounded-md border border-gray-200 p-3 hover:bg-gray-50"
                      data-testid={`export-item-${item.id}`}
                    >
                      <input
                        type="checkbox"
                        checked={selected.has(item.id)}
                        onChange={() => toggleItem(item.id)}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <div className="flex-1">
                        <span className="block text-sm font-medium text-gray-900">
                          {item.label}
                        </span>
                        <span className="text-xs text-gray-500">
                          {getItemFormat(item)}
                          {selected.size > 1 && ' · ZIP'}
                        </span>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Right: Format settings */}
              <div className="space-y-5">
                <p className="text-sm font-medium text-gray-900">
                  Format settings
                </p>

                {hasDataItemSelected && (
                  <div>
                    <p className="mb-2 text-sm font-medium text-gray-700">
                      Data table format
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      {(['excel', 'pdf'] as const).map((fmt) => (
                        <button
                          key={fmt}
                          type="button"
                          onClick={() => setDataFormat(fmt)}
                          className={twMerge([
                            'rounded-md border-2 p-2 text-center text-sm font-medium transition-all',
                            dataFormat === fmt
                              ? 'border-blue-500 bg-blue-50 text-blue-700'
                              : 'border-gray-300 text-gray-700 hover:border-gray-400',
                          ])}
                          data-testid={`data-format-${fmt}`}
                        >
                          {fmt === 'excel' ? 'Excel (.xlsx)' : 'PDF'}
                        </button>
                      ))}
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      Applies to data tables only
                    </p>
                  </div>
                )}

                <PaperSizeSelector
                  selectedSize={currentPaperSize}
                  onSelect={(size) => setExportSettings({ paperSize: size })}
                />
                <ResolutionSelector
                  selectedResolution={currentResolution}
                  onSelect={(resolution) => setExportSettings({ resolution })}
                />
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <Button
                type="button"
                color="gray"
                onClick={onClose}
                disabled={isExporting}
                data-testid="dialog-export-documentation-cancel-button"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={() => handleExport()}
                disabled={isExporting || selected.size === 0}
                data-testid="dialog-export-documentation-export-button"
                className="flex items-center justify-center"
              >
                {isExporting && <LoadingIcon className="mr-2" />}
                {isExporting ? 'Exporting...' : exportLabel}
              </Button>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}
